import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ethers } from "ethers";
import { toast } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import {
    FiLock
} from "react-icons/fi";
import { useWallet } from "../hooks/useWallet";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { API_BASE } from "../constants/index";
import { useTranslation } from "../hooks/useTranslation";
import ImgTotalStaked from "../images/icon-staking-staked.png";
import Img3dTotalStaked from "../images/total-staked-img.png";
import IconHistoryStaked from "../images/icon-stake-history.png";
import ImgClaimCheck from "../images/rewards-claimed-check.png";
import ImgClaimable from "../images/icon-staking-claimable.png";

const UNLOCK_FREE_STAKING_API = `${API_BASE}/publicApi/unlockFreeStakingV2`;
const FREE_STAKING_LIST = `${API_BASE}/publicApi/freeStakingList`;
const CLAIM_FREE_STAKING_API = `${API_BASE}/publicApi/claimFreeStakingBonus`;
const WITHDRAWAL_TOKEN_API = `${API_BASE}/publicApi/withdrawalToken`;
const MIN_FREE_STAKING_CLAIM_AMOUNT = 30;
const CLAIMS_DISABLED = true;

const getFreeStakingClaimAmount = () => 0;

function formatUsd(value) {
    const num = parseFloat(value);
    if (isNaN(num)) return "Unlisted";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(num);
}

export default function Staking() {
    const { t } = useTranslation();
    const { account, isConnected, isCorrectNetwork } = useWallet();
    const { executeWithFailover, getWalletSigner } = useRPCProvider();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // State management
    const [freeStakeAmount, setFreeStakeAmount] = useState(0);
    const [isLoadingClaimRewards, setIsLoadingClaimRewards] = useState(false);
    const [isLoadingUnlockReward, setIsLoadingUnlockReward] = useState(false);
    const [showProcessClaimModal, setShowProcessClaimModal] = useState(false);
    const [processClaimStep, setProcessClaimStep] = useState(1); // 1 = Processing, 2 = Success
    const [showProcessUnlockModal, setShowProcessUnlockModal] = useState(false);
    const [processUnlockStep, setProcessUnlockStep] = useState(1); // 1 = Processing, 2 = Success
    const [showClaimConfirmationModal, setShowClaimConfirmationModal] = useState(false);
    const [showUnlockConfirmationModal, setShowUnlockConfirmationModal] = useState(false);
    const [selectedStakingId, setSelectedStakingId] = useState(null);
    const [records, setRecords] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPendingActive, setTotalPendingActive] = useState(0);
    const [totalActiveBonus, setTotalActiveBonus] = useState(0);
    const [claimedRewardAmount, setClaimedRewardAmount] = useState("0");
    const itemsPerPage = 10;

    const generateUniqueId = () => {
        const now = Date.now().toString();
        const rand = Math.floor(Math.random() * 1_000_000)
            .toString()
            .padStart(6, "0");
        return now + rand;
    };

    const getFreeStakingRecords = useCallback(async () => {
        if (account) {
            try {
                const res = await fetch(FREE_STAKING_LIST, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        walletAddress: account,
                        page: currentPage,
                        limit: itemsPerPage
                    }),
                });
                const result = await res.json().catch(() => ({}));

                setFreeStakeAmount(result.total_staked || 0);
                setTotalPendingActive(result.total_bonus || 0);
                setTotalActiveBonus(0);

                if (result.count > 0) {
                    setRecords(result.data || []);
                    setTotalCount(result.total_records || result.count || 0);
                } else {
                    setRecords([]);
                    setTotalCount(0);
                }
            } catch (apiError) {
                console.warn("getFreeStaking API call failed:", apiError);
            }
        }
    }, [account, currentPage]);

    useEffect(() => {
        getFreeStakingRecords()
    }, [getFreeStakingRecords])

    const unlockReward = useCallback(async (stakingId) => {
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }
        setIsLoadingUnlockReward(true);
        try {
            setShowUnlockConfirmationModal(false);
            setShowProcessUnlockModal(true);
            setProcessUnlockStep(1);

            let responseData; // Define here to use in the while condition
            do {
                const uniqueId = generateUniqueId();

                const res = await fetch(UNLOCK_FREE_STAKING_API, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ walletAddress: account, uniqueId: uniqueId, stakingId: stakingId }),
                });

                responseData = await res.json().catch(() => ({}));

                if (!responseData.success) {
                    if (responseData.message === "This unlock has already been processed.") {
                        continue;
                    }

                    toast.error(responseData.message);
                    setShowProcessUnlockModal(false);
                    return;
                }

                setProcessUnlockStep(2);
                getFreeStakingRecords();
                break;

            } while (responseData?.message === "This unlock has already been processed.");

        } catch (error) {
            console.error("unlockReward error:", error);
            if (error.code === 4001) {
                toast.error(t("validation.transactionRejected"));
            } else {
                toast.error(
                    error?.reason ||
                    error?.shortMessage ||
                    error?.message ||
                    t("staking.failedUnlockReward")
                );
            }
        } finally {
            setIsLoadingUnlockReward(false);
        }
    }, [account, t, getFreeStakingRecords]);

    const getWithdrawalErrorMessage = useCallback((errorMessage) => {
        const message = String(errorMessage || "");

        if (message.includes("pending Withdrawal request")) {
            return t("freeStaking.withdrawalPendingRequest");
        }
        if (message.includes("Insufficient balance")) {
            return t("freeStaking.withdrawalInsufficientBalance");
        }
        if (message.includes("Invalid JSON") || message.includes("missing field")) {
            return t("freeStaking.withdrawalInvalidRequest");
        }
        if (message.includes("Method not allowed")) {
            return t("freeStaking.withdrawalMethodNotAllowed");
        }

        return message || t("freeStaking.failedWithdraw");
    }, [t]);

    const submitClaimWithdrawal = useCallback(async (amount) => {
        const withdrawalAmount = Number.parseFloat(amount);

        if (!Number.isFinite(withdrawalAmount) || withdrawalAmount <= 0) {
            throw new Error(t("freeStaking.claimWithdrawalInvalidAmount"));
        }

        if (withdrawalAmount < MIN_FREE_STAKING_CLAIM_AMOUNT) {
            throw new Error(t("validation.minimumClaimAmount", { minClaimAmount: MIN_FREE_STAKING_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_FREE_STAKING_CLAIM_AMOUNT}`);
        }

        const res = await fetch(WITHDRAWAL_TOKEN_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                walletAddress: account,
                amount: String(withdrawalAmount),
                uniqueId: generateUniqueId(),
            }),
        });

        const data = await res.json().catch(() => ({}));

        if (!data.success) {
            throw new Error(getWithdrawalErrorMessage(data.error || data.message));
        }

        return data;
    }, [account, t, getWithdrawalErrorMessage]);

    const claimReward = useCallback(async (stakingId) => {
        if (CLAIMS_DISABLED) return;
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }

        const selectedRecord = records.find((record) => String(record.mo_id) === String(stakingId));
        const claimAmount = getFreeStakingClaimAmount(selectedRecord);
        setClaimedRewardAmount(claimAmount);

        if (!Number.isFinite(claimAmount) || claimAmount < MIN_FREE_STAKING_CLAIM_AMOUNT) {
            toast.error(t("validation.minimumClaimAmount", { minClaimAmount: MIN_FREE_STAKING_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_FREE_STAKING_CLAIM_AMOUNT}`);
            return;
        }

        setIsLoadingClaimRewards(true);
        try {
            setShowClaimConfirmationModal(false);
            setShowProcessClaimModal(true);
            setProcessClaimStep(1);

            const claimUniqueId = generateUniqueId();

            const res = await fetch(CLAIM_FREE_STAKING_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ walletAddress: account, uniqueId: claimUniqueId, stakingId: stakingId }),
            });

            const responseData = await res.json().catch(() => ({}));

            if (!responseData.success) {
                toast.error(responseData.message || t("staking.failedUnlockReward"));
                setShowProcessClaimModal(false);
                return;
            }

            try {
                await submitClaimWithdrawal(claimAmount);
                toast.success(t("freeStaking.claimAndWithdrawalSuccess"));
            } catch (withdrawalError) {
                toast.error(`${t("freeStaking.claimCompletedWithdrawalFailed")}: ${withdrawalError.message}`);
            }

            setProcessClaimStep(2);
            getFreeStakingRecords();

        } catch (error) {
            console.error("claimReward error:", error);
            if (error.code === 4001) {
                toast.error(t("validation.transactionRejected"));
            } else {
                toast.error(
                    error?.reason ||
                    error?.shortMessage ||
                    error?.message ||
                    t("staking.failedUnlockReward")
                );
            }
        } finally {
            setIsLoadingClaimRewards(false);
        }
    }, [account, t, getFreeStakingRecords, records, submitClaimWithdrawal])



    return (
        <div className="relative overflow-x-hidden min-h-[calc(100vh-200px)]">
            {/* ── Staking Hero Section ── */}
            <div className="relative z-10 max-w-[550px] mx-auto pt-4 pb-2 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex-shrink-0 size-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.back")}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-semibold text-white truncate">{t("freeStaking.title") || "Staking NFT"}</h1>
                        <p className="text-sm text-gray-500 truncate">{t("staking.pageSubtitle") || "Track your earnings and manage your claim"}</p>
                    </div>
                </div>

                {/* Total Staked Card */}
                <div
                    className="rounded-2xl border border-[#2a1a42] overflow-hidden flex items-center justify-between p-5"
                >
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <span
                                className="size-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ background: 'rgba(124,58,237,0.25)' }}
                            >
                                <FiLock className="size-4 text-[#a78bfa]" />
                            </span>
                            <span className="text-[#7c6a9a] text-[11px] font-semibold uppercase tracking-widest">
                                {t("staking.totalStakedLabel") || "Total Staked"}
                            </span>
                        </div>
                        <p className="text-white font-extrabold text-2xl leading-none">
                            {freeStakeAmount}
                            <span className="text-[#7c6a9a] text-base font-semibold ml-2">USDT</span>
                        </p>
                    </div>
                    <img
                        src={Img3dTotalStaked}
                        alt="staked"
                        className="size-24 object-contain flex-shrink-0"
                        style={{ filter: 'drop-shadow(0 0 18px rgba(124,58,237,0.6))' }}
                    />
                </div>

                {/* Header row */}
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-white font-semibold text-xl">{t("staking.yourRewards") || "Your Rewards"}</h3>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    {/* Total Pending Base*/}
                    <div
                        className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                        style={{ background: 'rgba(124,58,237,0.08)' }}
                    >
                        <span
                            className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                        >
                            <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                        </span>
                        <div>
                            <p className="text-[#938F99] text-[12px] leading-tight">{t("freeStaking.totalPendingBase") || "Total Bonus"}</p>
                            <p className="text-white font-bold text-xl leading-none mt-2">
                                {formatUsd(totalPendingActive)}
                            </p>
                        </div>
                    </div>

                    {/* Total Active Base */}
                    <div
                        className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                        style={{ background: 'rgba(124,58,237,0.08)' }}
                    >
                        <span
                            className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                        >
                            <img src={ImgClaimable} alt="active" className="size-5 object-contain" />
                        </span>
                        <div>
                            <p className="text-[#938F99] text-[12px] leading-tight">{t("freeStaking.totalActiveBase") || "Claimable"}</p>
                            <div className="flex items-end gap-2 flex-wrap mt-2">
                                <p className="text-white font-bold text-xl leading-none">
                                    {formatUsd(totalActiveBonus)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    {records.map((record) => {
                        const claimAmount = getFreeStakingClaimAmount(record);
                        const canClaimMinimum = Number.isFinite(claimAmount) && claimAmount >= MIN_FREE_STAKING_CLAIM_AMOUNT;
                        const claimDisabled = CLAIMS_DISABLED || !record.can_claim || !canClaimMinimum || isLoadingClaimRewards;
                        return (
                        <div key={record.mo_id} className="rounded-xl p-4 border border-[#2a1a42] flex items-center justify-between transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60" style={{ background: 'rgba(124,58,237,0.08)' }}>
                            <div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                    <div className="flex items-baseline gap-1">
                                        <p className="text-white font-bold text-xl leading-none">
                                            {formatUsd(record.mo_amount)}
                                        </p>
                                        <span className="text-white/40 text-[10px] font-bold uppercase">USDT</span>
                                    </div>

                                    {!record.can_unlock && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <div className="px-2 py-0.5 rounded-md bg-[#facc15]/10 border border-[#facc15]/20 whitespace-nowrap">
                                                <p className="text-[#facc15] text-[10px] font-bold tracking-tight leading-none uppercase">{t("freeStaking.pendingBase")}: {formatUsd(record.pending_base)}</p>
                                            </div>
                                            <div className="px-2 py-0.5 rounded-md bg-[#4ade80]/10 border border-[#4ade80]/20 whitespace-nowrap">
                                                <p className="text-[#4ade80] text-[10px] font-bold tracking-tight leading-none uppercase">{t("freeStaking.activeBase")}: {formatUsd(record.active_base)}</p>
                                            </div>
                                        </div>
                                    )}

                                    {record.can_unlock && (
                                        <div className="flex items-center">
                                            <div className="px-2 py-0.5 rounded-md bg-[#ef4444]/10 border border-[#ef4444]/20 whitespace-nowrap">
                                                <p className="text-[#ef4444] text-[10px] font-bold tracking-tight leading-none uppercase">{t("freeStaking.thisStakingIsNotActive")}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="text-[#938F99] text-[12px] mt-1.5">{record.mo_cdate}</p>
                            </div>
                            {record.can_unlock ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedStakingId(record.mo_id);
                                        setShowUnlockConfirmationModal(true);
                                    }}
                                    disabled={!record.can_unlock || isLoadingUnlockReward}
                                    className={`px-5 py-2 rounded-full font-medium text-sm transition-all duration-300 ${record.can_unlock ? 'bg-[#5B47CD] text-white hover:opacity-80' : 'bg-[#2a1a42] text-[#938F99] cursor-not-allowed'}`}
                                >
                                    {t("staking.active") || "Active"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedStakingId(record.mo_id);
                                        setShowClaimConfirmationModal(true);
                                    }}
                                    disabled={claimDisabled}
                                    title={!canClaimMinimum ? (t("validation.minimumClaimAmount", { minClaimAmount: MIN_FREE_STAKING_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_FREE_STAKING_CLAIM_AMOUNT}`) : undefined}
                                    className={`px-5 py-2 rounded-full font-medium text-sm transition-all duration-300 ${!claimDisabled ? 'bg-[#5B47CD] text-white hover:opacity-80' : 'bg-[#2a1a42] text-[#938F99] cursor-not-allowed'}`}
                                >
                                    {t("staking.claim") || "Claim"}
                                </button>
                            )}
                        </div>
                        );
                    })}
                    {records.length === 0 && (
                        <div className="rounded-xl p-6 border border-[#2a1a42] flex flex-col items-center justify-center text-center" style={{ background: 'rgba(124,58,237,0.08)' }}>
                            <p className="text-[#938F99]">{t("freeStaking.noHistory") || "No Records Yet"}</p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalCount > itemsPerPage && (
                    <div className="flex items-center justify-center gap-4 mt-6 pb-8">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="size-10 rounded-full border border-[#2a1a42] flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#7c3aed]/10 transition-colors"
                        >
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="text-white font-bold">{currentPage}</span>
                            <span className="text-[#938F99]">/</span>
                            <span className="text-[#938F99]">{Math.ceil(totalCount / itemsPerPage)}</span>
                        </div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalCount / itemsPerPage)))}
                            disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                            className="size-10 rounded-full border border-[#2a1a42] flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#7c3aed]/10 transition-colors"
                        >
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Claim Confirmation Modal */}
            <AnimatePresence>
                {showClaimConfirmationModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-[100] bg-[#05010D]/80 backdrop-blur-sm flex items-center justify-center p-4"
                    >
                        <div className="max-w-[400px] w-full bg-[#110A1F] border border-[#2a1a42] rounded-3xl p-6 relative shadow-[0_0_40px_rgba(124,58,237,0.15)]">
                            <button
                                onClick={() => setShowClaimConfirmationModal(false)}
                                className="absolute top-4 right-4 text-[#938F99] hover:text-white transition-colors"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>

                            <div className="flex flex-col items-center text-center mt-2">
                                <div className="size-16 rounded-full bg-[#7c3aed]/20 flex items-center justify-center mb-4 border border-[#7c3aed]/30">
                                    <span className="material-symbols-outlined text-[#a78bfa] text-3xl">redeem</span>
                                </div>
                                <h3 className="text-white font-semibold text-xl mb-2">{t("staking.confirmClaim") || "Confirm Claim"}</h3>
                                <p className="text-[#938F99] text-sm mb-8 leading-relaxed px-2">
                                    {t("staking.confirmClaimDesc") || "Are you sure you want to claim your available rewards? The amount will be transferred to your wallet."}
                                </p>

                                <div className="w-full flex gap-3">
                                    <button
                                        onClick={() => setShowClaimConfirmationModal(false)}
                                        className="flex-1 py-3.5 rounded-full border border-[#2a1a42] text-white font-medium hover:bg-[#2a1a42]/50 transition-colors"
                                    >
                                        {t("common.cancel") || "Cancel"}
                                    </button>
                                    <button
                                        onClick={() => {
                                            claimReward(selectedStakingId);
                                        }}
                                        className="flex-1 py-3.5 rounded-full bg-[#5B47CD] text-white font-bold hover:bg-[#4C3BBA] hover:shadow-[0_0_15px_rgba(91,71,205,0.4)] transition-all active:scale-[0.98]"
                                    >
                                        {t("common.confirm") || "Confirm"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* New Process Claim Modal */}
            <AnimatePresence>
                {showProcessClaimModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-[100] bg-[#05010D] text-white"
                    >
                        <div className="max-w-[550px] w-full mx-auto min-h-screen flex flex-col relative">
                            {/* Header */}
                            <div className="flex items-center gap-4 p-5">
                                <button onClick={() => setShowProcessClaimModal(false)} className="material-symbols-outlined text-white text-2xl">arrow_back</button>
                                <h2 className="text-xl font-medium">{t("staking.claimReward")}</h2>
                            </div>

                            {/* Content */}
                            <div
                                className="flex-1 flex flex-col items-center p-6 relative overflow-y-auto max-h-[calc(90vh-100px)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#888]/40 hover:[&::-webkit-scrollbar-thumb]:bg-[#888]/60 [&::-webkit-scrollbar-thumb]:rounded-full"
                            >
                                {processClaimStep === 1 ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col gap-3 items-center w-full max-w-[550px] mx-auto my-auto"
                                    >
                                        <div className="relative size-[140px] mb-12">
                                            <div className="absolute inset-0 rounded-full border-[4px] border-[#2A1A42]"></div>
                                            <div className="absolute inset-0 rounded-full border-[4px] border-transparent border-t-[#A855F7] border-r-[#A855F7] animate-[spin_1.5s_linear_infinite] drop-shadow-[0_0_15px_rgba(168,85,247,0.6)]"></div>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[60px] text-[#A855F7] drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]">hourglass_empty</span>
                                            </div>
                                        </div>
                                        <h3 className="text-[28px] font-semibold mb-3">{t("staking.processing")}</h3>
                                        <p className="text-[#938F99] text-center text-sm leading-relaxed max-w-[240px]">
                                            {t("freeStaking.processingClaimAndWithdrawal") || "Please wait while we process your claim and withdrawal request."}
                                        </p>

                                        {/* Bottom Warning */}
                                        <div className="bg-[#110A1F] rounded-2xl p-4 flex items-center gap-3 border border-[#2A1A42] w-full mx-auto">
                                            <span className="material-symbols-outlined text-[#8B5CF6]">gpp_good</span>
                                            <p className="text-[13px] text-[#938F99] leading-tight">{t("staking.thisMayTakeAwhilePleaseDoNotClose")}</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col items-center w-full max-w-md mx-auto my-auto"
                                    >
                                        <div className="size-[100px] rounded-full bg-gradient-to-b from-[#A855F7] to-[#4C1D95] p-[4px] mb-6 shadow-[0_0_40px_rgba(168,85,247,0.4)] mt-4">
                                            <div className="w-full h-full bg-[#1A0B2E] rounded-full flex items-center justify-center shadow-[inset_0_0_20px_rgba(168,85,247,0.5)]">
                                                <img src={ImgClaimCheck} alt="" style={{ width: '100px', height: '100px' }} />
                                            </div>
                                        </div>

                                        <h3 className="text-[28px] font-semibold mb-3 text-center">{t("freeStaking.rewardsClaimedWithdrawalSubmitted") || t("staking.rewardsClaimed")}</h3>
                                        <p className="text-[#938F99] text-center text-sm mb-6 max-w-[280px] leading-relaxed">
                                            {t("freeStaking.yourRewardsHaveBeenClaimedAndWithdrawalSubmitted") || t("staking.yourRewardsHaveBeenClaimed")}
                                        </p>

                                        {/* Receipt Card */}
                                        <div className="w-full bg-transparent border border-[#2A1A42] rounded-3xl p-6 mb-8 relative overflow-hidden">
                                            <p className="text-[#938F99] text-center text-[15px] mb-2">{t("staking.youHaveReceived")}</p>
                                            <p className="text-white text-center text-[40px] font-bold mb-6 flex justify-center items-baseline gap-2">
                                                {formatUsd(claimedRewardAmount).replace(/^\$/, "")} <span className="text-[16px] font-medium text-[#938F99]">aBnbUSDT</span>
                                            </p>

                                            <div className="flex justify-center mb-8 relative">
                                                <div className="absolute inset-0 bg-[#A855F7] opacity-20 blur-2xl rounded-full"></div>
                                                <img src={Img3dTotalStaked} className="size-[125px] object-contain relative z-10" alt="Tokens" />
                                            </div>

                                            <div className="flex justify-between border-t border-[#2A1A42] pt-5">
                                                <div className="w-px bg-[#2A1A42] self-stretch mx-2"></div>
                                                <div className="flex items-start gap-3 w-1/2 pl-2">
                                                    <span className="material-symbols-outlined text-[#A855F7] text-2xl flex-shrink-0" style={{ fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}>deployed_code</span>
                                                    <div>
                                                        <p className="text-[#5b4a75] text-[11px] font-medium mb-1">{t("common.network")}</p>
                                                        <p className="text-white text-[12px] font-bold">aBnBUsdt</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* What's Next */}
                                        <div className="w-full text-left mt-auto mb-6">
                                            <h4 className="text-[22px] font-semibold text-white mb-4">{t('staking.whatNext')}</h4>
                                            <div className="flex items-center justify-between cursor-pointer group">
                                                <div className="flex items-center gap-4">
                                                    <div className="size-12 rounded-full bg-[#1A0B2E] border border-[#2A1A42] flex items-center justify-center group-hover:border-[#A855F7] transition-colors">
                                                        <img src={ImgTotalStaked} className="size-6" alt="Stake" />
                                                    </div>
                                                    <div>
                                                        <p className="text-white text-[15px] font-bold mb-0.5">{t('staking.stakeMore')}</p>
                                                        <p className="text-[#5b4a75] text-[13px]">{t('staking.stakeMoreTokenToEarnMoreReward')}</p>
                                                    </div>
                                                </div>
                                                <span onClick={() => { setShowProcessClaimModal(false) }} className="material-symbols-outlined text-[#938F99] group-hover:text-white transition-colors text-2xl">chevron_right</span>
                                            </div>
                                        </div>

                                    </motion.div>

                                )}
                            </div>

                            {/* Done Button for Step 2 */}
                            {processClaimStep === 2 && (
                                <div className="p-5 w-full max-w-md mx-auto mt-auto flex-shrink-0">
                                    <button
                                        onClick={() => setShowProcessClaimModal(false)}
                                        className="w-full h-14 rounded-full bg-[#5B47CD] text-white font-bold text-lg hover:bg-[#4C3BBA] active:scale-[0.98] transition-all"
                                    >
                                        {t('common.done')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Unlock Process Modal */}
            <AnimatePresence>
                {showProcessUnlockModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-[120] bg-[#05010D] text-white"
                    >
                        <div className="max-w-[550px] w-full mx-auto min-h-screen flex flex-col relative">
                            {/* Header */}
                            <div className="flex items-center gap-4 p-5">
                                <button onClick={() => setShowProcessUnlockModal(false)} className="material-symbols-outlined text-white text-2xl">arrow_back</button>
                                <h2 className="text-xl font-medium">{t("freeStaking.unlockReward")}</h2>
                            </div>

                            {/* Content */}
                            <div
                                className="flex-1 flex flex-col items-center p-6 relative overflow-y-auto max-h-[calc(90vh-100px)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#888]/40 hover:[&::-webkit-scrollbar-thumb]:bg-[#888]/60 [&::-webkit-scrollbar-thumb]:rounded-full"
                            >
                                {processUnlockStep === 1 ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col gap-3 items-center w-full max-w-[550px] mx-auto my-auto"
                                    >
                                        <div className="relative size-[140px] mb-12">
                                            <div className="absolute inset-0 rounded-full border-[4px] border-[#2A1A42]"></div>
                                            <div className="absolute inset-0 rounded-full border-[4px] border-transparent border-t-[#A855F7] border-r-[#A855F7] animate-[spin_1.5s_linear_infinite] drop-shadow-[0_0_15px_rgba(168,85,247,0.6)]"></div>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[60px] text-[#A855F7] drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]">lock_open</span>
                                            </div>
                                        </div>
                                        <h3 className="text-[28px] font-semibold mb-3">{t("freeStaking.unlocking")}</h3>
                                        <p className="text-[#938F99] text-center text-sm leading-relaxed max-w-[240px]">
                                            {t("freeStaking.pleaseWaitWhileWeProcessYourUnlock")}
                                        </p>

                                        {/* Bottom Warning */}
                                        <div className="bg-[#110A1F] rounded-2xl p-4 flex items-center gap-3 border border-[#2A1A42] w-full mx-auto">
                                            <span className="material-symbols-outlined text-[#8B5CF6]">gpp_good</span>
                                            <p className="text-[13px] text-[#938F99] leading-tight">{t("staking.thisMayTakeAwhilePleaseDoNotClose")}</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col items-center w-full max-w-md mx-auto my-auto"
                                    >
                                        <div className="size-[100px] rounded-full bg-gradient-to-b from-[#A855F7] to-[#4C1D95] p-[4px] mb-6 shadow-[0_0_40px_rgba(168,85,247,0.4)] mt-4">
                                            <div className="w-full h-full bg-[#1A0B2E] rounded-full flex items-center justify-center shadow-[inset_0_0_20px_rgba(168,85,247,0.5)]">
                                                <img src={ImgClaimCheck} alt="" style={{ width: '100px', height: '100px' }} />
                                            </div>
                                        </div>

                                        <h3 className="text-[28px] font-semibold mb-3 text-center">{t("freeStaking.rewardsUnlocked")}</h3>
                                        <p className="text-[#938F99] text-center text-sm mb-6 max-w-[280px] leading-relaxed">
                                            {t("freeStaking.yourRewardsHaveBeenUnlocked")}
                                        </p>

                                        {/* What's Next */}
                                        <div className="w-full text-left mt-auto mb-6">
                                            <h4 className="text-[22px] font-semibold text-white mb-4">{t('staking.whatNext')}</h4>
                                            <div className="flex items-center justify-between cursor-pointer group">
                                                <div className="flex items-center gap-4">
                                                    <div className="size-12 rounded-full bg-[#1A0B2E] border border-[#2A1A42] flex items-center justify-center group-hover:border-[#A855F7] transition-colors">
                                                        <img src={ImgTotalStaked} className="size-6" alt="Stake" />
                                                    </div>
                                                    <div>
                                                        <p className="text-white text-[15px] font-bold mb-0.5">{t('staking.stakeMore')}</p>
                                                        <p className="text-[#5b4a75] text-[13px]">{t('staking.stakeMoreTokenToEarnMoreReward')}</p>
                                                    </div>
                                                </div>
                                                <span onClick={() => { setShowProcessUnlockModal(false) }} className="material-symbols-outlined text-[#938F99] group-hover:text-white transition-colors text-2xl">chevron_right</span>
                                            </div>
                                        </div>

                                    </motion.div>

                                )}
                            </div>

                            {/* Done Button for Step 2 */}
                            {processUnlockStep === 2 && (
                                <div className="p-5 w-full max-w-md mx-auto mt-auto flex-shrink-0">
                                    <button
                                        onClick={() => setShowProcessUnlockModal(false)}
                                        className="w-full h-14 rounded-full bg-[#5B47CD] text-white font-bold text-lg hover:bg-[#4C3BBA] active:scale-[0.98] transition-all"
                                    >
                                        {t('common.done')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Unlock Confirmation Modal */}
            <AnimatePresence>
                {showUnlockConfirmationModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[110] bg-[#05010D]/90 backdrop-blur-md flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="max-w-[420px] w-full bg-[#110A1F] border border-[#7c3aed]/30 rounded-[32px] p-8 relative overflow-hidden shadow-[0_0_50px_rgba(124,58,237,0.2)]"
                        >
                            {/* Decorative background glow */}
                            <div className="absolute -top-24 -right-24 size-48 bg-[#7c3aed]/20 blur-[80px] rounded-full"></div>
                            <div className="absolute -bottom-24 -left-24 size-48 bg-[#5B47CD]/20 blur-[80px] rounded-full"></div>

                            <button
                                onClick={() => setShowUnlockConfirmationModal(false)}
                                className="absolute top-6 right-6 text-[#938F99] hover:text-white transition-colors z-10"
                            >
                                <span className="material-symbols-outlined text-2xl">close</span>
                            </button>

                            <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="size-20 rounded-3xl bg-gradient-to-br from-[#7c3aed] to-[#5B47CD] flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(124,58,237,0.4)] rotate-3">
                                    <span className="material-symbols-outlined text-white text-4xl -rotate-3 font-light">lock_open</span>
                                </div>

                                <h3 className="text-white font-semibold text-2xl mb-3 tracking-tight">{t("freeStaking.unlockReward")}</h3>
                                <p className="text-[#938F99] text-sm mb-10 leading-relaxed px-4">
                                    {t("freeStaking.readyToUnlockYourRewards")}
                                </p>

                                <div className="w-full flex flex-col gap-3">
                                    <button
                                        onClick={() => {
                                            unlockReward(selectedStakingId);
                                        }}
                                        className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#7c3aed] to-[#5B47CD] text-white font-bold text-lg hover:shadow-[0_0_25px_rgba(124,58,237,0.5)] transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                    >
                                        {t("freeStaking.confirmUnlock")}
                                    </button>
                                    <button
                                        onClick={() => setShowUnlockConfirmationModal(false)}
                                        className="w-full py-4 rounded-2xl bg-[#1A0B2E] border border-[#2a1a42] text-[#938F99] font-medium hover:text-white hover:border-[#7c3aed]/50 transition-all"
                                    >
                                        {t("common.cancel")}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>

    );
}
